import React, { useState, useEffect, useCallback } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { useToast } from '../contexts/ToastContext';
import { documentsAPI, playersAPI } from '../services/api';
import { DocumentListItem, Document as DocumentType } from '../types/document';
import EntityDocumentsList from '../components/documents/EntityDocumentsList';
import UploadDocumentForm from '../components/documents/UploadDocumentForm';
import SlideOver from '../components/ui/SlideOver';
import LazyLoadTrigger from '../components/ui/LazyLoadTrigger';
import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { isAdmin, isStaff, isPlayer, isParent } from '../types/auth';
import { useBatchedList } from '../hooks/useBatchedList';
import SendToSchoolModal from '../components/schools/SendToSchoolModal';

type TabType = 'players' | 'staff' | 'public' | 'my-documents';

interface InlineUploadTarget {
  playerId?: number;
  userId?: number;
  isPublic: boolean;
  entityName?: string;
}

const Documents: React.FC = () => {
  const { user } = useAuth();
  usePageTitle('Dokumentumok');
  const { showToast } = useToast();
  // Melyik dokumentumot küldjük épp az iskolának
  const [schoolSendDocId, setSchoolSendDocId] = useState<number | null>(null);

  // Determine initial tab based on user role
  const getInitialTab = (): TabType => {
    if (!user) return 'my-documents';
    if (isStaff(user)) return 'players';
    return 'my-documents';
  };

  const [activeTab, setActiveTab] = useState<TabType>(getInitialTab());
  const [playersList, setPlayersList] = useState<DocumentListItem[]>([]);
  const [staffList, setStaffList] = useState<DocumentListItem[]>([]);
  const [publicDocuments, setPublicDocuments] = useState<DocumentType[]>([]);
  const [myDocuments, setMyDocuments] = useState<DocumentType[]>([]);
  const [myPlayersList, setMyPlayersList] = useState<DocumentListItem[]>([]); // For parent users to select child
  const [playerSearchTerm, setPlayerSearchTerm] = useState('');
  const [staffSearchTerm, setStaffSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const canDeleteDocument = (doc: DocumentType): boolean => {
    if (!user) return false;
    return isAdmin(user) || doc.uploadedByUserId === user.id;
  };

  // Beúszó panel (kiválasztott játékos/stábtag dokumentumai)
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerDocuments, setViewerDocuments] = useState<DocumentType[]>([]);
  const [viewerEntityName, setViewerEntityName] = useState('');
  const [viewerPlayerId, setViewerPlayerId] = useState<number | undefined>();
  const [viewerUserId, setViewerUserId] = useState<number | undefined>();
  const [panelUploadOpen, setPanelUploadOpen] = useState(false);

  // Beágyazott feltöltő panel a Közös / Saját füleken
  const [inlineUpload, setInlineUpload] = useState<InlineUploadTarget | null>(null);

  const loadPlayersList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await documentsAPI.getPlayerDocumentList(playerSearchTerm || undefined);
      setPlayersList(data);
    } catch (error) {
      console.error('Failed to load players list:', error);
    } finally {
      setLoading(false);
    }
  }, [playerSearchTerm]);

  const loadStaffList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await documentsAPI.getUserDocumentList(staffSearchTerm || undefined);
      setStaffList(data);
    } catch (error) {
      console.error('Failed to load staff list:', error);
    } finally {
      setLoading(false);
    }
  }, [staffSearchTerm]);

  const loadPublicDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await documentsAPI.getPublicDocuments();
      setPublicDocuments(data);
    } catch (error) {
      console.error('Failed to load public documents:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMyDocuments = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      // Egyszerre több forrásból is jöhetnek dokumentumok (pl. Játékos ÉS
      // Szülő egyszerre) - mindet összegyűjtjük, nem csak az elsőt egy
      // kizáró if/else-if láncból.
      const allDocs: DocumentType[] = [];
      const playerEntries: DocumentListItem[] = [];

      if (isPlayer(user) || isParent(user)) {
        const players = await playersAPI.getAll();

        if (isPlayer(user)) {
          const myPlayer = players.find((p: any) => p.userId === user.id);
          if (myPlayer) {
            const data = await documentsAPI.getDocumentsByPlayerId(myPlayer.id);
            allDocs.push(...data);
            playerEntries.push({ entityId: myPlayer.id, name: myPlayer.name, documentCount: data.length });
          }
        }

        if (isParent(user)) {
          const myChildren = players.filter((p: any) => p.parentUserId === user.id || p.secondParentUserId === user.id);
          for (const child of myChildren) {
            const docs = await documentsAPI.getDocumentsByPlayerId(child.id);
            allDocs.push(...docs);
            playerEntries.push({ entityId: child.id, name: child.name, documentCount: docs.length });
          }
        }
      }

      // Nem-admin stábtagnak a saját (nem játékoshoz kötött) dokumentumai is
      // idekerülnek - a feltöltés-gomb választása lejjebb egyelőre a
      // player-listát részesíti előnyben, ha az nem üres.
      if (isStaff(user) && !isAdmin(user)) {
        const data = await documentsAPI.getDocumentsByUserId(user.id);
        allDocs.push(...data);
      }

      setMyDocuments(allDocs);
      setMyPlayersList(playerEntries);
    } catch (error) {
      console.error('Failed to load my documents:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    setInlineUpload(null);
    if (activeTab === 'players') {
      loadPlayersList();
    } else if (activeTab === 'staff') {
      loadStaffList();
    } else if (activeTab === 'public') {
      loadPublicDocuments();
    } else if (activeTab === 'my-documents') {
      loadMyDocuments();
    }
  }, [activeTab, loadPlayersList, loadStaffList, loadPublicDocuments, loadMyDocuments]);

  const handleSelectPlayer = async (playerId: number) => {
    try {
      const documents = await documentsAPI.getDocumentsByPlayerId(playerId);
      const player = playersList.find(p => p.entityId === playerId);
      setViewerDocuments(documents);
      setViewerEntityName(player?.name || '');
      setViewerPlayerId(playerId);
      setViewerUserId(undefined);
      setPanelUploadOpen(false);
      setViewerOpen(true);
    } catch (error) {
      console.error('Failed to load player documents:', error);
    }
  };

  const handleSelectStaff = async (userId: number) => {
    try {
      const documents = await documentsAPI.getDocumentsByUserId(userId);
      const staffUser = staffList.find(u => u.entityId === userId);
      setViewerDocuments(documents);
      setViewerEntityName(staffUser?.name || '');
      setViewerPlayerId(undefined);
      setViewerUserId(userId);
      setPanelUploadOpen(false);
      setViewerOpen(true);
    } catch (error) {
      console.error('Failed to load staff documents:', error);
    }
  };

  const handleDeleteDocument = async (id: number) => {
    try {
      await documentsAPI.deleteDocument(id);
      setViewerDocuments(viewerDocuments.filter(d => d.id !== id));

      // Refresh the lists
      if (activeTab === 'players') {
        loadPlayersList();
      } else if (activeTab === 'staff') {
        loadStaffList();
      } else if (activeTab === 'public') {
        setPublicDocuments(publicDocuments.filter(d => d.id !== id));
      } else if (activeTab === 'my-documents') {
        loadMyDocuments();
      }
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  /** A beúszó panelben történt feltöltés után frissíti a panel tartalmát és a listát. */
  const handlePanelUploadSuccess = async () => {
    setPanelUploadOpen(false);
    try {
      if (viewerPlayerId) {
        setViewerDocuments(await documentsAPI.getDocumentsByPlayerId(viewerPlayerId));
        loadPlayersList();
      } else if (viewerUserId) {
        setViewerDocuments(await documentsAPI.getDocumentsByUserId(viewerUserId));
        loadStaffList();
      }
    } catch (error) {
      console.error('Failed to refresh documents:', error);
    }
  };

  const handleInlineUploadSuccess = () => {
    setInlineUpload(null);
    if (activeTab === 'public') {
      loadPublicDocuments();
    } else if (activeTab === 'my-documents') {
      loadMyDocuments();
    }
  };

  // Define tabs based on user role
  const getAvailableTabs = () => {
    const tabs: Array<{ id: TabType; name: string }> = [];

    if (user && isAdmin(user)) {
      // Admin can see all: Players, Staff, Public
      tabs.push({ id: 'players', name: 'Játékosok' });
      tabs.push({ id: 'staff', name: 'Stáb' });
      tabs.push({ id: 'public', name: 'Közös' });
    } else if (user && isStaff(user)) {
      // Coach can see: Players (filtered by age groups), Public
      tabs.push({ id: 'players', name: 'Játékosok' });
      tabs.push({ id: 'public', name: 'Közös' });
    }

    // Player, Parent, and Staff (non-Admin) users see My Documents tab
    if (user && (isPlayer(user) || isParent(user) || (isStaff(user) && !isAdmin(user)))) {
      tabs.push({ id: 'my-documents', name: 'Saját dokumentumaim' });
    }

    return tabs;
  };

  const tabs = getAvailableTabs();

  // Mobile batched lists (lazy load görgetésre)
  const publicBatch = useBatchedList(publicDocuments, 5);
  const myBatch = useBatchedList(myDocuments, 5);

  const formatFileSize = (bytes: number): string => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Játékoshoz tartozó, fájllal rendelkező dokumentum küldhető az iskolának.
  // A szerver ellenőrzi, hogy a felhasználó a sajátját / látható játékosét küldi-e.
  const canSendToSchool = (doc: DocumentType) => !!doc.playerId && doc.fileSize > 0;

  const sentToSchoolMeta = (doc: DocumentType) =>
    doc.sentToSchoolAt ? (
      <span className="text-green-700">
        Iskolának elküldve: {new Date(doc.sentToSchoolAt).toLocaleDateString('hu-HU')}
      </span>
    ) : null;

  const handleSentToSchool = () => {
    const sentAt = new Date().toISOString();
    const mark = (docs: DocumentType[]) =>
      docs.map((d) => (d.id === schoolSendDocId ? { ...d, sentToSchoolAt: sentAt } : d));
    setViewerDocuments(mark);
    setMyDocuments(mark);
    setSchoolSendDocId(null);
  };

  // Egy dokumentum-kártya (desktop lista és a beúszó panel)
  const renderDocumentCard = (doc: DocumentType, extraMeta?: React.ReactNode) => (
    <div
      key={doc.id}
      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 break-words">{doc.originalFileName}</h4>
          {doc.description && (
            <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
            <span>Feltöltve: {new Date(doc.uploadedAt).toLocaleDateString('hu-HU')}</span>
            {extraMeta}
            {sentToSchoolMeta(doc)}
          </div>
        </div>
        <div className="flex gap-2 sm:ml-4">
          {canSendToSchool(doc) && (
            <button
              onClick={() => setSchoolSendDocId(doc.id)}
              className="inline-flex flex-1 sm:flex-initial items-center justify-center px-3 py-2 border border-primary-300 shadow-sm text-sm leading-4 font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50"
            >
              Iskolának
            </button>
          )}
          <button
            onClick={async () => {
              try {
                await documentsAPI.downloadDocument(doc.id, doc.originalFileName);
              } catch (error) {
                console.error('Failed to download document:', error);
                showToast('Hiba történt a fájl letöltése során', 'error');
              }
            }}
            className="inline-flex flex-1 sm:flex-initial items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Letöltés
          </button>
          {canDeleteDocument(doc) && (
            <button
              onClick={() => {
                if (window.confirm('Biztosan törölni szeretnéd ezt a dokumentumot?')) {
                  handleDeleteDocument(doc.id);
                }
              }}
              className="inline-flex flex-1 sm:flex-initial items-center justify-center px-3 py-2 border border-red-300 shadow-sm text-sm leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
            >
              Törlés
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Kompakt dokumentum-kártya mobilra (2 oszlopos rács, md alatt)
  const renderMobileDocumentCard = (doc: DocumentType, extraMeta?: React.ReactNode) => (
    <div
      key={doc.id}
      className="border border-gray-200 rounded-lg p-3 flex flex-col hover:bg-gray-50 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-gray-900 break-words">{doc.originalFileName}</h4>
        {doc.description && (
          <p className="text-xs text-gray-600 mt-1">{doc.description}</p>
        )}
        <div className="mt-1.5 space-y-0.5 text-xs text-gray-500">
          <div className="truncate">Feltöltve: {new Date(doc.uploadedAt).toLocaleDateString('hu-HU')}</div>
          {extraMeta && <div className="truncate">{extraMeta}</div>}
          {doc.sentToSchoolAt && <div className="truncate">{sentToSchoolMeta(doc)}</div>}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 border-t border-gray-100 pt-1.5">
        {canSendToSchool(doc) && (
          <button
            onClick={() => setSchoolSendDocId(doc.id)}
            className="inline-flex flex-1 items-center justify-center px-2 py-1.5 border border-primary-300 shadow-sm text-xs leading-4 font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50"
          >
            Iskolának
          </button>
        )}
        <button
          onClick={async () => {
            try {
              await documentsAPI.downloadDocument(doc.id, doc.originalFileName);
            } catch (error) {
              console.error('Failed to download document:', error);
              showToast('Hiba történt a fájl letöltése során', 'error');
            }
          }}
          className="inline-flex flex-1 items-center justify-center px-2 py-1.5 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Letöltés
        </button>
        {canDeleteDocument(doc) && (
          <button
            onClick={() => {
              if (window.confirm('Biztosan törölni szeretnéd ezt a dokumentumot?')) {
                handleDeleteDocument(doc.id);
              }
            }}
            className="inline-flex flex-1 items-center justify-center px-2 py-1.5 border border-red-300 shadow-sm text-xs leading-4 font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
          >
            Törlés
          </button>
        )}
      </div>
    </div>
  );

  // Beágyazott feltöltő panel (Közös / Saját fülek)
  const renderInlineUploadPanel = () =>
    inlineUpload && (
      <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold text-gray-900">
          Dokumentum feltöltése{inlineUpload.entityName ? ` – ${inlineUpload.entityName}` : ''}
        </h3>
        <UploadDocumentForm
          playerId={inlineUpload.playerId}
          userId={inlineUpload.userId}
          isPublic={inlineUpload.isPublic}
          onSuccess={handleInlineUploadSuccess}
          onCancel={() => setInlineUpload(null)}
        />
      </div>
    );

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dokumentumok</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
              `}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'players' && (
        <EntityDocumentsList
          items={playersList}
          onSelectEntity={handleSelectPlayer}
          searchTerm={playerSearchTerm}
          onSearchChange={setPlayerSearchTerm}
          loading={loading}
        />
      )}

      {activeTab === 'staff' && (
        <EntityDocumentsList
          items={staffList}
          onSelectEntity={handleSelectStaff}
          searchTerm={staffSearchTerm}
          onSearchChange={setStaffSearchTerm}
          loading={loading}
        />
      )}

      {activeTab === 'public' && (
        <div>
          {/* Feltöltés: beágyazott panel popup helyett */}
          {inlineUpload ? (
            renderInlineUploadPanel()
          ) : (
            <div className="mb-4">
              <button
                onClick={() => setInlineUpload({ isPublic: true, entityName: 'Közös dokumentumok' })}
                className="inline-flex w-full sm:w-auto items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                Dokumentum feltöltése
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Betöltés...</div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              {publicDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">Még nincsenek közös dokumentumok.</p>
                </div>
              ) : (
                <div className="px-4 py-5 sm:p-6">
                  {/* Mobile: lazy loaded list (below md) */}
                  <div className="md:hidden grid grid-cols-2 gap-3">
                    {publicBatch.visible.map((doc) =>
                      renderMobileDocumentCard(doc, <span>Feltöltötte: {doc.uploadedByUserName}</span>)
                    )}
                    {publicBatch.hasMore && (
                      <LazyLoadTrigger
                        key={publicBatch.visible.length}
                        onLoadMore={publicBatch.loadMore}
                        className="col-span-2"
                      />
                    )}
                  </div>
                  {/* Desktop: full list (hidden below md) */}
                  <div className="hidden md:block space-y-3">
                    {publicDocuments.map((doc) =>
                      renderDocumentCard(doc, <span>Feltöltötte: {doc.uploadedByUserName}</span>)
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'my-documents' && (
        <div>
          {/* Feltöltés: beágyazott panel popup helyett */}
          {inlineUpload ? (
            renderInlineUploadPanel()
          ) : (
            <div className="mb-4">
              {myPlayersList.length === 1 ? (
                <button
                  onClick={() =>
                    setInlineUpload({
                      playerId: myPlayersList[0].entityId,
                      isPublic: false,
                      entityName: myPlayersList[0].name,
                    })
                  }
                  className="inline-flex w-full sm:w-auto items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                  Dokumentum feltöltése
                </button>
              ) : myPlayersList.length > 1 ? (
                <div className="flex flex-wrap gap-2">
                  {myPlayersList.map((player) => (
                    <button
                      key={player.entityId}
                      onClick={() =>
                        setInlineUpload({
                          playerId: player.entityId,
                          isPublic: false,
                          entityName: player.name,
                        })
                      }
                      className="inline-flex w-full sm:w-auto items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                      Feltöltés: {player.name}
                    </button>
                  ))}
                </div>
              ) : user && isStaff(user) && !isAdmin(user) ? (
                <button
                  onClick={() => setInlineUpload({ userId: user.id, isPublic: false })}
                  className="inline-flex w-full sm:w-auto items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                  Dokumentum feltöltése
                </button>
              ) : null}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Betöltés...</div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              {myDocuments.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">Még nincsenek dokumentumok.</p>
                </div>
              ) : (
                <div className="px-4 py-5 sm:p-6">
                  {/* Mobile: lazy loaded list (below md) */}
                  <div className="md:hidden grid grid-cols-2 gap-3">
                    {myBatch.visible.map((doc) =>
                      renderMobileDocumentCard(
                        doc,
                        doc.playerName ? <span>Játékos: {doc.playerName}</span> : undefined
                      )
                    )}
                    {myBatch.hasMore && (
                      <LazyLoadTrigger
                        key={myBatch.visible.length}
                        onLoadMore={myBatch.loadMore}
                        className="col-span-2"
                      />
                    )}
                  </div>
                  {/* Desktop: full list (hidden below md) */}
                  <div className="hidden md:block space-y-3">
                    {myDocuments.map((doc) =>
                      renderDocumentCard(
                        doc,
                        doc.playerName ? <span>Játékos: {doc.playerName}</span> : undefined
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Kiválasztott játékos/stábtag dokumentumai: jobbról beúszó panel */}
      <SlideOver
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        title={`Dokumentumok – ${viewerEntityName}`}
        maxWidthClass="sm:max-w-xl"
      >
        <div className="space-y-4">
          {panelUploadOpen ? (
            <div className="rounded-lg border border-gray-200 p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900">Dokumentum feltöltése</h3>
              <UploadDocumentForm
                playerId={viewerPlayerId}
                userId={viewerUserId}
                isPublic={false}
                onSuccess={handlePanelUploadSuccess}
                onCancel={() => setPanelUploadOpen(false)}
              />
            </div>
          ) : (
            <button
              onClick={() => setPanelUploadOpen(true)}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <CloudArrowUpIcon className="h-5 w-5 mr-2" />
              Dokumentum feltöltése
            </button>
          )}

          {viewerDocuments.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">
              Még nincsenek dokumentumok feltöltve.
            </p>
          ) : (
            <div className="space-y-3">
              {viewerDocuments.map((doc) =>
                renderDocumentCard(
                  doc,
                  <>
                    <span>Feltöltötte: {doc.uploadedByUserName}</span>
                    <span>Méret: {formatFileSize(doc.fileSize)}</span>
                  </>
                )
              )}
            </div>
          )}
        </div>
      </SlideOver>

      <SendToSchoolModal
        documentId={schoolSendDocId}
        onClose={() => setSchoolSendDocId(null)}
        onSent={handleSentToSchool}
      />
    </div>
  );
};

export default Documents;
